import _ from 'lodash'
import React, { useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CSSTransition } from 'react-transition-group'
import {Button, Input, Search, SearchResultProps} from 'semantic-ui-react'
import { SearchProps, SearchResultData } from 'semantic-ui-react/dist/commonjs/modules/Search/Search'
import { cancelAddressSearchRequest } from '../../apis/AddressServiceAPI'
import {
  AddressSearchResultsData,
  areAddressesLoading,
  getAddresses,
  getCandidateAddresses,
} from '../../common/candidateAddresses.slice'
import { useAppDispatch, useAppSelector } from '../../common/store'
import { replaceSpaceWithX } from '../../common/util'

import { LAYER_AERIAL_PHOTOGRAPHY_PATH } from '../../environment'
import {
  getSearchValue,
  updateAPProjectDetails,
  updateSearchValue,
} from './ap.slice'

import './SearchBar.css'
import APAdvancedSearchComponent from './APAdvancedSearchComponent'
import {APSearchUtils} from "./ap.searchUtil";
import {clearSelectFrame} from "./apFrames.slice";

let performAddressSearch = _.debounce(
  (value, dispatch) => {
    dispatch(getCandidateAddresses(value))
    dispatch(updateSearchValue(value))
  }, 300, {
    leading: true,
    trailing: true,
  },
)

function SearchBar() {

  const dispatch = useAppDispatch()
  const candidateAddresses = useAppSelector(getAddresses)
  const isLoading = useAppSelector(areAddressesLoading)
  const searchValue = useAppSelector(getSearchValue)
  const history = useHistory()
  const searchContainer = useRef<HTMLDivElement>(null)
  const [minChar, setMinChar] = useState<number>(2)

  const [debouncedSearch] = useState(() => {
    return _.debounce(performAddressSearch, 500, {
      leading: false,
      trailing: true,
    })
  })

  /**
   * semantic-ui-react Search callback
   *
   * Called when a result is selected
   *
   * @param e
   * @param result
   */
  const handleResultSelect = (e: React.MouseEvent<HTMLDivElement>, { result }: SearchResultData) => {
    let { laiskey } = result
    let parcel = candidateAddresses.find(parcel => parcel.LAIS_KEY === laiskey)

    handleSearchValueClear()
    //dispatch(resetAPSearchValue())
    dispatch(updateAPProjectDetails(null))
    dispatch(updateSearchValue(parcel?.SearchTerm))

    // we need to do this in case the user has selected a frame
    dispatch(clearSelectFrame())

    let url = `${LAYER_AERIAL_PHOTOGRAPHY_PATH}/${replaceSpaceWithX(laiskey)}`

    // We clear the URL here and set the new location minus any params.
    // Params are set when we select a frame in the APDataTable
    history.replace(url)
    searchContainer?.current?.getElementsByTagName('input')?.item(0)?.blur()
  }

  const handleSearchValueClear = () => {
    history.push(LAYER_AERIAL_PHOTOGRAPHY_PATH)
  }

  const onClickSearchClear = () => {
    handleSearchValueClear()
  }

  /**
   * semantic-ui-react Search callback
   *
   * Called for every item in the array of results to be rendered.
   *
   * @param props
   * @returns {*}
   */
  const ResultRenderer = ({ title, description }: SearchResultProps) => {
    return (
      <>
        <div className={'parcelSearchResultItem float-left'}>{title}</div>
        <div className={'description float-right'}>{description}</div>
      </>
    )
  }

  /**
   * semantic-ui-react Search callback
   *
   * Called when the input has changed
   *
   * @param e
   * @param value
   */
  const handleSearchChange = (e: React.MouseEvent<HTMLElement>, { value }: SearchProps) => {
    dispatch(updateSearchValue(value))
    cancelAddressSearchRequest()
    debouncedSearch.cancel()
    if (value && value.length >= 3) {
      performAddressSearch(value, dispatch)
    } else if (!value) {
      onClickSearchClear()
    }
  }

  const showSearchResults = () => {
    setMinChar(0)
    searchContainer?.current?.getElementsByTagName('input')?.item(0)?.focus()
  }

  return (
    <CSSTransition
      appear={true}
      in={true}
      exit={false}
      timeout={2000}
      classNames="parcel-search-bar">

      <div className={'apSearchContainer'} ref={searchContainer}>
        <div className={'searchBar'}>
          <Search className={'apSearch'}
                  placeholder="Enter address, parcel details or location..."
                  loading={isLoading}
                  onResultSelect={handleResultSelect}
                  onSearchChange={handleSearchChange}
                  results={APSearchUtils.getResults(candidateAddresses)}
                  resultRenderer={ResultRenderer}
                  size={'big'}
                  value={searchValue}
                  noResultsMessage="No aerial photographs found"
                  noResultsDescription="Enter parcel details"
                  minCharacters={minChar}
                  input={<Input input={{spellCheck: 'false', type: 'search'}}/>}
          />
        </div>
        <div className={'searchOption'}>
          <APAdvancedSearchComponent showSearchResults={showSearchResults}/>
        </div>
      </div>
    </CSSTransition>
  )

}

export default SearchBar





